import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBuyerTracker, useBulkAction, type BulkAction, type Milestone, type OfferStage, type TrackerRow } from "@/hooks/useBuyerTracker";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { formatMultipleRange } from "@shared/offerMath";
import { OfferTermsDialog, type OfferQueueItem } from "./OfferTermsDialog";
import { ReviewOffersModal } from "./ReviewOffersModal";
import { CimPackageDialog } from "./CimPackageDialog";
import { SendCimDialog } from "./SendCimDialog";
import { SendCimPicker, type SendCimQueueItem } from "./SendCimPicker";
import { useCimPackage } from "@/hooks/useCimPackage";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const MILES: { key: Milestone; label: string }[] = [
  { key: "teaser", label: "Teaser" }, { key: "nda", label: "NDA" }, { key: "cim", label: "CIM" },
  { key: "ioi", label: "IOI" }, { key: "loi", label: "LOI" },
];

function ymd(v: string | null) { return v ? new Date(v).toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) : null; }

function badge(row: TrackerRow): { text: string; cls: string } {
  if (row.status === "passed") return { text: "Passed", cls: "text-red-600 dark:text-red-400" };
  if (row.ndaExecutedAt) return { text: "Live", cls: "text-green-700 dark:text-green-400" };
  return { text: "Awaiting", cls: "text-slate-400" };
}

export function BuyerTrackerGrid({ clientId, onAddClick }: { clientId: string; onAddClick: () => void }) {
  const [q, setQ] = useState<{
    page: number; pageSize: number; sort: string; order: "asc" | "desc";
    search: string; status: "all" | "active" | "passed"; milestones: Milestone[];
  }>({ page: 1, pageSize: 50, sort: "created", order: "desc", search: "", status: "all", milestones: [] });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [backdate, setBackdate] = useState<{ m: Milestone; date: string } | null>(null);
  const [offerDialog, setOfferDialog] = useState<{ stage: OfferStage; queue: OfferQueueItem[]; date?: string } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [cimBuilderOpen, setCimBuilderOpen] = useState(false);
  const [cimPickerOpen, setCimPickerOpen] = useState(false);
  const [sendCim, setSendCim] = useState<{ queue: SendCimQueueItem[]; index: number } | null>(null);
  const { data: cimPkg } = useCimPackage(clientId);
  const cimReady = !!cimPkg?.package;
  const { data, isLoading } = useBuyerTracker(clientId, q);
  const bulk = useBulkAction(clientId);
  const todayYmd = new Date().toISOString().slice(0, 10);

  const rows = data?.rows ?? [];
  const toggle = (id: string) => {
    setAllMatching(false);
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearSel = () => { setSelected(new Set()); setAllMatching(false); };
  const sortBy = (col: string) => setQ((p) => ({ ...p, sort: col, order: p.sort === col && p.order === "asc" ? "desc" : "asc", page: 1 }));
  const selectionPayload = () => allMatching
    ? { selectAllMatching: true, filters: { search: q.search || undefined, status: q.status === "all" ? undefined : q.status, milestones: q.milestones.length ? q.milestones : undefined } }
    : { linkIds: [...selected] };
  const selectedCount = allMatching ? (data?.totalRows ?? 0) : selected.size;
  const labelFor = (m: Milestone) => MILES.find((x) => x.key === m)!.label;

  // Reverse the exact rows just affected (ids come back from the server), so undo
  // stays precise even when "select all matching" was used.
  const undoById = (ids: string[], action: BulkAction, doneMsg: string) => {
    if (!ids.length) return;
    bulk.mutate({ linkIds: ids, action }, { onSuccess: () => toast.success(doneMsg) });
  };

  // IOI/LOI marks require offer terms — route through the dialog (steps through
  // each selected buyer). Teaser/NDA/CIM keep the instant bulk path.
  const openOfferDialog = (stage: OfferStage, dateIso?: string) => {
    if (allMatching) {
      toast.info(`Select specific buyers to mark ${stage.toUpperCase()} — terms are entered per buyer`);
      return;
    }
    const sel = rows.filter((r) => selected.has(r.linkId));
    if (!sel.length) return;
    setOfferDialog({
      stage,
      date: dateIso,
      queue: sel.map((r) => ({
        linkId: r.linkId, buyerName: r.buyerName,
        existing: stage === "ioi" ? r.ioiTerms : r.loiTerms,
        ioiRef: r.ioiTerms,
        alreadyMarked: !!(stage === "ioi" ? r.ioiReceivedAt : r.loiReceivedAt),
      })),
    });
  };

  const editTerms = (r: TrackerRow, stage: OfferStage) => {
    setOfferDialog({
      stage,
      queue: [{
        linkId: r.linkId, buyerName: r.buyerName,
        existing: stage === "ioi" ? r.ioiTerms : r.loiTerms,
        ioiRef: r.ioiTerms,
        alreadyMarked: true,
      }],
    });
  };

  const runMilestone = (m: Milestone, unset: boolean) => {
    if (!unset && (m === "ioi" || m === "loi")) { openOfferDialog(m); return; }
    const label = labelFor(m);
    bulk.mutate(
      { ...selectionPayload(), action: { type: "milestone", milestone: m, unset } },
      {
        onSuccess: (res) => {
          clearSel();
          if (!res.affected) return;
          const n = res.affected;
          const verb = unset ? "Unmarked" : "Marked";
          toast.success(`${verb} ${label} for ${n} buyer${n === 1 ? "" : "s"}`, {
            duration: 8000,
            action: {
              label: "Undo",
              onClick: () =>
                undoById(res.ids, { type: "milestone", milestone: m, unset: !unset }, `Reverted ${label}`),
            },
          });
        },
      },
    );
  };

  const runStatus = (status: "active" | "passed") => {
    bulk.mutate(
      { ...selectionPayload(), action: { type: "status", status } },
      {
        onSuccess: (res) => {
          clearSel();
          if (!res.affected) return;
          const n = res.affected;
          const msg =
            status === "passed"
              ? `Marked passed: ${n} buyer${n === 1 ? "" : "s"}`
              : `Set to active: ${n} buyer${n === 1 ? "" : "s"}`;
          toast.success(msg, {
            duration: 8000,
            action: {
              label: "Undo",
              onClick: () =>
                undoById(res.ids, { type: "status", status: status === "passed" ? "active" : "passed" }, "Reverted"),
            },
          });
        },
      },
    );
  };
  const runRemove = (payload: ReturnType<typeof selectionPayload> = selectionPayload(), name?: string) => {
    bulk.mutate(
      { ...payload, action: { type: "remove" } },
      {
        onSuccess: (res) => {
          clearSel();
          if (!res.affected) return;
          const n = res.affected;
          const what = name && n === 1 ? name : `${n} buyer${n === 1 ? "" : "s"}`;
          toast.success(`Removed ${what} from tracker`, {
            duration: 8000,
            action: { label: "Undo", onClick: () => undoById(res.ids, { type: "restore" }, "Restored") },
          });
        },
      },
    );
  };

  // Backdated mark: send local-noon so the date lands the same in UTC and Central.
  const runMilestoneDated = (m: Milestone, dateYmd: string) => {
    if (m === "ioi" || m === "loi") { openOfferDialog(m, `${dateYmd}T12:00:00`); return; }
    const label = labelFor(m);
    bulk.mutate(
      { ...selectionPayload(), action: { type: "milestone", milestone: m, date: `${dateYmd}T12:00:00` } },
      {
        onSuccess: (res) => {
          clearSel();
          if (!res.affected) return;
          const n = res.affected;
          toast.success(`Marked ${label} (${dateYmd}) for ${n} buyer${n === 1 ? "" : "s"}`, {
            duration: 8000,
            action: {
              label: "Undo",
              onClick: () => undoById(res.ids, { type: "milestone", milestone: m, unset: true }, `Reverted ${label}`),
            },
          });
        },
      },
    );
  };

  const exportUrl = `/api/companies/${clientId}/buyer-tracker/export?status=${q.status}&sort=${q.sort}&order=${q.order}${q.search ? `&search=${encodeURIComponent(q.search)}` : ""}${q.milestones.length ? `&milestones=${q.milestones.join(",")}` : ""}`;

  const toggleMilestoneFilter = (m: Milestone) => {
    clearSel();
    setQ((p) => ({
      ...p,
      page: 1,
      milestones: p.milestones.includes(m) ? p.milestones.filter((x) => x !== m) : [...p.milestones, m],
    }));
  };
  const milestoneFilterLabel = q.milestones.length === 0
    ? "Reached: any"
    : `Reached: ${MILES.filter((m) => q.milestones.includes(m.key)).map((m) => m.label).join(", ")}`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden dark:border-slate-900">
      <div className="flex items-center gap-3 border-b border-slate-100 p-4 dark:border-slate-900">
        <div className="text-[15px] font-bold text-slate-900 dark:text-slate-200">
          Buyers in Market <span className="font-medium text-slate-400">· {data?.totalRows ?? 0}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select
            className="h-8 rounded-md border border-slate-200 px-2 text-xs dark:border-slate-900"
            value={q.status}
            onChange={(e) => { clearSel(); setQ((p) => ({ ...p, status: e.target.value as "all" | "active" | "passed", page: 1 })); }}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="passed">Passed</option>
          </select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`h-8 rounded-md border px-2 text-xs ${q.milestones.length ? "border-blue-300 bg-blue-50 font-semibold text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-400" : "border-slate-200 dark:border-slate-900"}`}>
                {milestoneFilterLabel} ▾
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {MILES.map((m) => (
                <DropdownMenuCheckboxItem
                  key={m.key}
                  checked={q.milestones.includes(m.key)}
                  onCheckedChange={() => toggleMilestoneFilter(m.key)}
                  onSelect={(e) => e.preventDefault()}
                >
                  Reached {m.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={q.milestones.length === 0}
                onClick={() => { clearSel(); setQ((p) => ({ ...p, milestones: [], page: 1 })); }}
              >
                Clear — show all
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Input
            className="h-8 w-44"
            placeholder="Search buyers"
            value={q.search}
            onChange={(e) => { clearSel(); setQ((p) => ({ ...p, search: e.target.value, page: 1 })); }}
          />
          <Button variant="outline" size="sm" onClick={() => setCimBuilderOpen(true)}>
            CIM Package
            {cimPkg && (
              cimReady
                ? <span className="ml-1.5 font-bold text-green-600 dark:text-green-400">✓</span>
                : <span className="ml-1.5 rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">not set up</span>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            title={cimReady ? "Send the CIM package to buyers" : "Set up the CIM package first"}
            onClick={() => (cimReady ? setCimPickerOpen(true) : setCimBuilderOpen(true))}
          >
            Send CIM
          </Button>
          {((data?.aggregates.iois ?? 0) + (data?.aggregates.lois ?? 0)) > 0 && (
            <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)}>
              Review Offers
              <span className="ml-1.5 rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                {(data?.aggregates.iois ?? 0) + (data?.aggregates.lois ?? 0)}
              </span>
            </Button>
          )}
          <a href={exportUrl}><Button variant="outline" size="sm">Export to Excel</Button></a>
          <Button size="sm" onClick={onAddClick}>+ Add buyers</Button>
        </div>
      </div>

      {(selected.size > 0 || allMatching) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2.5 text-sm dark:border-blue-900 dark:bg-blue-950/40">
          <span className="font-bold text-blue-700 dark:text-blue-400">{selectedCount} selected{allMatching ? " (all matching)" : ""}</span>
          {!allMatching && data && selected.size === rows.length && data.totalRows > rows.length && (
            <button className="text-xs font-semibold text-blue-600 underline dark:text-blue-400" onClick={() => setAllMatching(true)}>
              Select all {data.totalRows} matching
            </button>
          )}
          <span className="text-blue-300">|</span>
          {MILES.map((m) => (
            <Button key={m.key} variant="outline" size="sm" className="h-7 text-xs" disabled={bulk.isPending} onClick={() => runMilestone(m.key, false)}>
              Mark {m.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={bulk.isPending}
            onClick={() => runStatus("passed")}
          >
            Mark passed
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={bulk.isPending}>Unmark ▾</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {MILES.map((m) => (
                <DropdownMenuItem key={m.key} onClick={() => runMilestone(m.key, true)}>
                  Unmark {m.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => runStatus("active")}>Set back to Active</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={bulk.isPending}>Mark as of… ▾</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {MILES.map((m) => (
                <DropdownMenuItem key={m.key} onClick={() => setBackdate({ m: m.key, date: todayYmd })}>
                  {m.label} as of…
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-red-600 hover:text-red-700 dark:text-red-400"
            disabled={bulk.isPending}
            onClick={() => runRemove()}
          >
            Remove
          </Button>
          <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={clearSel}>Clear</Button>
        </div>
      )}

      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400 dark:bg-slate-950/40">
            <th className="w-9 p-2.5 text-center">
              <input
                type="checkbox"
                checked={rows.length > 0 && selected.size === rows.length}
                onChange={(e) => {
                  setAllMatching(false);
                  setSelected(e.target.checked ? new Set(rows.map((r) => r.linkId)) : new Set());
                }}
              />
            </th>
            <th className="cursor-pointer p-2.5" onClick={() => sortBy("buyer")}>
              Buyer{q.sort === "buyer" ? (q.order === "asc" ? " ▲" : " ▼") : ""}
            </th>
            {MILES.map((m) => (
              <th key={m.key} className="cursor-pointer p-2.5 text-center" onClick={() => sortBy(m.key)}>
                {m.label}{q.sort === m.key ? (q.order === "asc" ? " ▲" : " ▼") : ""}
              </th>
            ))}
            <th className="cursor-pointer p-2.5 text-right pr-4" onClick={() => sortBy("status")}>
              Status{q.sort === "status" ? (q.order === "asc" ? " ▲" : " ▼") : ""}
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Loading…</td></tr>}
          {!isLoading && rows.length === 0 && (
            <tr><td colSpan={8} className="p-6 text-center text-slate-400">No buyers yet. Add buyers to start tracking.</td></tr>
          )}
          {rows.map((r) => {
            const b = badge(r);
            const dates: Record<Milestone, string | null> = {
              teaser: r.teaserSentAt, nda: r.ndaExecutedAt, cim: r.cimSentAt,
              ioi: r.ioiReceivedAt, loi: r.loiReceivedAt,
            };
            return (
              <tr
                key={r.linkId}
                className={`group border-t border-slate-100 ${selected.has(r.linkId) ? "bg-blue-50 dark:bg-blue-950/40" : ""} ${r.status === "passed" ? "opacity-60" : ""}`}
              >
                <td className="p-2.5 text-center">
                  <input type="checkbox" checked={selected.has(r.linkId)} onChange={() => toggle(r.linkId)} />
                </td>
                <td className="p-2.5">
                  <div className="font-semibold text-slate-900 dark:text-slate-200">{r.buyerName}</div>
                  <div className="text-[11px] text-slate-400">{[r.buyerFirmType, r.buyerHQ].filter(Boolean).join(" · ")}</div>
                </td>
                {MILES.map((m) => {
                  const isOffer = m.key === "ioi" || m.key === "loi";
                  const terms = m.key === "ioi" ? r.ioiTerms : m.key === "loi" ? r.loiTerms : null;
                  return (
                    <td key={m.key} className="p-2.5 text-center">
                      {dates[m.key]
                        ? <span className="rounded-md border border-green-100 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:border-green-900 dark:bg-green-950/40 dark:text-green-400">{ymd(dates[m.key])}</span>
                        : <span className="text-slate-300">—</span>}
                      {isOffer && dates[m.key] && (
                        terms ? (
                          <button
                            title={`Edit ${m.label} terms`}
                            className="mt-1 block w-full text-[11px] font-bold tabular-nums text-blue-700 hover:underline dark:text-blue-400"
                            onClick={() => editTerms(r, m.key as OfferStage)}
                          >
                            {formatMultipleRange(terms.multipleLow, terms.multipleHigh)}
                          </button>
                        ) : (
                          <button
                            title={`${m.label} marked without terms — click to add`}
                            className="mt-1 block w-full rounded bg-amber-50 px-1 text-[10px] font-semibold text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-400 dark:bg-amber-900/30"
                            onClick={() => editTerms(r, m.key as OfferStage)}
                          >
                            + terms
                          </button>
                        )
                      )}
                    </td>
                  );
                })}
                <td className="p-2.5 text-right pr-4">
                  <div className="flex items-center justify-end gap-2">
                    {r.ndaExecutedAt && !r.cimSentAt && r.status !== "passed" && (
                      <button
                        className="rounded-md border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-bold text-blue-700 hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-400 dark:bg-blue-900/30"
                        title="Send the CIM package to this buyer"
                        onClick={() => setSendCim({ queue: [{ linkId: r.linkId, buyerId: r.buyerId, buyerName: r.buyerName, ndaMissing: false }], index: 0 })}
                      >
                        Send CIM
                      </button>
                    )}
                    <span className={`text-[11px] font-semibold ${b.cls}`}>● {b.text}</span>
                    <button
                      title="Remove from tracker"
                      className="text-slate-300 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100 dark:text-red-400"
                      onClick={() => runRemove({ linkIds: [r.linkId] }, r.buyerName)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {data && data.totalRows > q.pageSize && (
        <div className="flex items-center justify-between border-t border-slate-100 p-3 text-xs text-slate-500 dark:border-slate-900">
          <span>Page {data.page} · {data.totalRows} buyers</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={q.page <= 1} onClick={() => setQ((p) => ({ ...p, page: p.page - 1 }))}>Prev</Button>
            <Button variant="outline" size="sm" disabled={q.page * q.pageSize >= data.totalRows} onClick={() => setQ((p) => ({ ...p, page: p.page + 1 }))}>Next</Button>
          </div>
        </div>
      )}

      {offerDialog && (
        <OfferTermsDialog
          open
          stage={offerDialog.stage}
          queue={offerDialog.queue}
          clientId={clientId}
          ebitda={data?.ebitda ?? null}
          date={offerDialog.date}
          onDone={() => { setOfferDialog(null); clearSel(); }}
        />
      )}

      <ReviewOffersModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        clientId={clientId}
        onEdit={(stage, item) => setOfferDialog({ stage, queue: [item] })}
      />

      <CimPackageDialog open={cimBuilderOpen} onClose={() => setCimBuilderOpen(false)} clientId={clientId} />
      <SendCimPicker
        open={cimPickerOpen}
        onClose={() => setCimPickerOpen(false)}
        clientId={clientId}
        onStart={(queue) => setSendCim({ queue, index: 0 })}
      />
      {sendCim && sendCim.queue[sendCim.index] && (() => {
        const item = sendCim.queue[sendCim.index];
        const advance = () => setSendCim((p) =>
          p && p.index + 1 < p.queue.length ? { ...p, index: p.index + 1 } : null,
        );
        return (
          <SendCimDialog
            open
            onClose={() => setSendCim(null)}
            clientId={clientId}
            linkId={item.linkId}
            buyerId={item.buyerId}
            buyerName={item.buyerName}
            ndaMissing={item.ndaMissing}
            stepLabel={sendCim.queue.length > 1 ? `Buyer ${sendCim.index + 1} of ${sendCim.queue.length}` : undefined}
            onSent={advance}
            onNeedsPackage={() => setCimBuilderOpen(true)}
          />
        );
      })()}

      <Dialog open={!!backdate} onOpenChange={(o) => !o && setBackdate(null)}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Mark {backdate ? labelFor(backdate.m) : ""} as of</DialogTitle>
          </DialogHeader>
          <input
            type="date"
            className="h-9 rounded-md border border-slate-200 px-2 text-sm dark:border-slate-900"
            value={backdate?.date ?? ""}
            max={todayYmd}
            onChange={(e) => setBackdate((p) => (p ? { ...p, date: e.target.value } : p))}
          />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setBackdate(null)}>Cancel</Button>
            <Button
              size="sm"
              disabled={!backdate?.date || bulk.isPending}
              onClick={() => {
                if (backdate?.date) {
                  runMilestoneDated(backdate.m, backdate.date);
                  setBackdate(null);
                }
              }}
            >
              Mark
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
